import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import {Download, ShieldCheck} from 'lucide-react'
import { useProducts } from '@/hooks/useProducts'

export default function ProductsPage() {
  const { products, loading, fetchProducts } = useProducts()

  useEffect(() => {
    fetchProducts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Page Header */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            WHMCS Plugins
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Professional, secure integrations for your WHMCS installation. All plugins are officially licensed and verified.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-slate-600">Loading products...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8">
              {products.map((product) => (
                <div
                  key={product._id}
                  className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                      <div className="flex items-center space-x-3 mb-4">
                        <ShieldCheck className="w-6 h-6 text-green-500" />
                        <span className="text-sm font-semibold text-green-700">Official Licensed</span>
                      </div>
                      
                      {product.imageUrl && (
                        <img 
                          src={product.imageUrl} 
                          alt={product.name}
                          className="w-full h-48 object-cover rounded-lg mb-4"
                        />
                      )}
                      
                      <h2 className="text-2xl font-bold text-slate-900 mb-3">{product.name}</h2>
                      <p className="text-slate-600 mb-6">{product.shortDescription}</p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {product.features?.slice(0, 4).map((feature, index) => (
                          <span
                            key={index}
                            className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center space-x-2 text-sm text-slate-500">
                        <Download className="w-4 h-4" />
                        <span>Instant digital download</span>
                      </div>
                    </div>

                    <div className="flex flex-col justify-between">
                      <div>
                        <p className="text-3xl font-bold text-slate-900 mb-2">${product.price}</p>
                        <p className="text-sm text-slate-500 mb-6">One-time payment</p>
                      </div>
                      
                      <div className="space-y-3">
                        <Link
                          to={`/product/${product.slug}`}
                          className="block w-full text-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          View Details
                        </Link>
                        <button className="block w-full text-center px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-colors">
                          Add to Cart
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  )
}
